// Replacement for console logger
//
// info level will only log to the console console
// assert, warning, error and exception will alse log to Sentry

import {assert as assertWrapper} from '@one-click-lca/3d-buildings-shared';
// @sentry/minimal is the platform independant import; it can be imported either
// by node or browser processes.
// Sentry.init still needs to be done by the proper platform-specific import
import * as Sentry from '@sentry/minimal';
import {User, Severity} from '@sentry/types';

type Context = Record<string, unknown>;

//////////////////////////////////////////
// Logging messages, errors and exceptions

const LOG_TO_SENTRY = false;

export function info(errorStr: string, context?: Context): void {
  console.info(errorStr, context || '');
}

export function warning(errorStr: string, context?: Context): void {
  console.warn(errorStr, context || '');
  if (LOG_TO_SENTRY) {
    logger(errorStr, Severity.Warning, context);
  }
}

export function error(errorStr: string, context?: Context): void {
  console.error(errorStr, context || '');
  if (LOG_TO_SENTRY) {
    logger(errorStr, Severity.Error, context);
  }
}

export function exception(exception: Error | string, context?: Context): void {
  console.error(exception, context || '');
  captureException(exception, context);
}

export function logErrorToSentry(errorStr: string, context?: Context): void {
  logger(errorStr, Severity.Error, context);
}

export function assert(
  expression: unknown,
  errorStr?: string,
  context?: Context,
): asserts expression {
  if (!expression) {
    if (context) {
      Sentry.configureScope(scope => {
        scope.setExtras(context);
      });
    }
    assertWrapper(expression, errorStr);
  }
}

//////////////////////////////////////////
// Context management
export function addExtraContext(extraContext: Context): void {
  Sentry.setExtras(extraContext);
}

export function setUserContext(userContext: User | null): void {
  Sentry.setUser(userContext);
}

export function clearUserContext(): void {
  Sentry.setUser(null);
}

//////////////////////////////////////////
// Private implementation
function logger(
  errorStr: string,
  level: Severity,
  context?: Context,
  errorName = '',
): void {
  const error = new Error(errorStr);
  if (errorName) {
    error.name = errorName;
  }
  Sentry.withScope(scope => {
    scope.setFingerprint([errorStr]);
    if (context) {
      scope.setExtras(context);
    }
    scope.setLevel(level);
    Sentry.captureException(error);
  });
}

function captureException(exception: Error | string, context?: Context): void {
  Sentry.withScope(scope => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureException(exception);
  });
}
